import { useJitsu } from "@jitsu/jitsu-react"
import LeafIcon from "../components/icons/LeafIcon"
import WhatsAppIcon from "./icons/WhatsAppIcon"
import InstagramIcon from "./icons/InstagramIcon"
import { getOrCreateSessionId } from "../lib/session"

type Props = {
  className?: string
}

export default function Footer({ className }: Props) {
  const { analytics } = useJitsu()

  function trackClick(label: string) {
    analytics.track("external_link_click", {
      session_id: getOrCreateSessionId(),
      label,
    })
  }

  return (
    <footer
      className={[
        "relative w-full overflow-hidden",
        className ?? "",
      ].join(" ")}
    >
      {/* Background watermark */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 overflow-hidden">
        <img
          src="/logo-gray.webp"
          className="h-auto w-full object-contain p-2"
          alt="Logo cinza"
          loading="lazy"
          decoding="async"
        />
      </div>

      <div className="relative z-10 px-6 sm:px-0 sm:mx-auto sm:max-w-[90%]">
        {/* Top row */}
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          {/* MOBILE ONLY */}
          <div className="grid grid-cols-2 items-start gap-y-4 md:hidden">
            {/* Left side */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 font-sans text-[10px] sm:text-sm">
                <WhatsAppIcon size={18} className="text-primary-darker shrink-0" />
                <a
                  href="https://api.whatsapp.com/send/?phone=5548996064932&text=Ol%C3%A1%2C+vim+do+site+e+gostaria+de+mais+informa%C3%A7%C3%B5es&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer hover:underline"
                  onClick={() => trackClick("whatsapp")}
                >
                  48 99606-4932
                </a>
              </div>

              <div className="flex items-center gap-3 font-sans text-[10px] sm:text-sm">
                <InstagramIcon size={18} className="text-primary-darker shrink-0" />
                <a
                  href="https://www.instagram.com/nativamed/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer hover:underline"
                  onClick={() => trackClick("instagram")}
                >
                  @nativamed
                </a>
              </div>
            </div>

            {/* Right side */}
            <div className="flex flex-row self-center items-end justify-end gap-3">
              <a
                href="https://www.google.com/search?sca_esv=c50016dede84fb5a&sxsrf=ANbL-n4zdT1fVfb4KiWuuPaHj3eOd9X-tQ:1773339396637&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOYENaj6n3Oblr20hyaz0tw6FmVtwUU37rtk0e_O3sAAvToyLO9BlspZI8MNDx51vetRQyxDnEQwLyIgK4azQchd_OQX1zwHQKOmjWRfQLA1lV4mEGoUmVSc5SQuI4sjx1D95sR4%3D&q=nativamed+%7C+Consultora+de+Sa%C3%BAde+Coment%C3%A1rios&sa=X&ved=2ahUKEwi43rWG_JqTAxWklJUCHXO8BYIQ0bkNegQIOBAF&cshid=1773339431410742&biw=1600&bih=773&dpr=1"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
                onClick={() => trackClick("google_reviews")}
              >
                <img
                  alt="Avaliações google"
                  loading="lazy"
                  decoding="async"
                  src="/google-stars.webp"
                  className="h-8 object-cover"
                />
              </a>

              <a
                href="https://www.reclameaqui.com.br/empresa/nativamed-telemedicina-ltda/"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
                onClick={() => trackClick("reclame_aqui")}
              >
                <img
                  alt="Reclame aqui"
                  loading="lazy"
                  decoding="async"
                  src="/reclame-aqui.webp"
                  className="h-8 object-cover"
                />
              </a>
            </div>
          </div>

          {/* DESKTOP / TABLET */}
          <div className="hidden w-full md:flex md:items-start md:justify-between">
            {/* Left side */}
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-3 font-sans text-sm">
                <WhatsAppIcon size={18} className="text-primary-darker" />
                <a
                  href="https://api.whatsapp.com/send/?phone=5548996064932&text=Ol%C3%A1%2C+vim+do+site+e+gostaria+de+mais+informa%C3%A7%C3%B5es&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer hover:underline"
                  onClick={() => trackClick("whatsapp")}
                >
                  48 99606-4932
                </a>
              </div>

              <div className="flex items-center gap-3 font-sans text-sm">
                <InstagramIcon size={18} className="text-primary-darker" />
                <a
                  href="https://www.instagram.com/nativamed/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer hover:underline"
                  onClick={() => trackClick("instagram")}
                >
                  @nativamed
                </a>
              </div>
            </div>

            {/* Right side */}
            <div className="flex flex-col gap-4 md:items-end">
              <div className="flex items-center gap-3 font-sans text-sm">
                <a
                  href="https://www.google.com/search?sca_esv=c50016dede84fb5a&sxsrf=ANbL-n4zdT1fVfb4KiWuuPaHj3eOd9X-tQ:1773339396637&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOYENaj6n3Oblr20hyaz0tw6FmVtwUU37rtk0e_O3sAAvToyLO9BlspZI8MNDx51vetRQyxDnEQwLyIgK4azQchd_OQX1zwHQKOmjWRfQLA1lV4mEGoUmVSc5SQuI4sjx1D95sR4%3D&q=nativamed+%7C+Consultora+de+Sa%C3%BAde+Coment%C3%A1rios&sa=X&ved=2ahUKEwi43rWG_JqTAxWklJUCHXO8BYIQ0bkNegQIOBAF&cshid=1773339431410742&biw=1600&bih=773&dpr=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-16 cursor-pointer"
                  onClick={() => trackClick("google_reviews")}
                >
                  <img
                    alt="Avaliações google"
                    loading="lazy"
                    decoding="async"
                    className="h-full"
                    src="/google-stars.webp"
                  />
                </a>

                <a
                  href="https://www.reclameaqui.com.br/empresa/nativamed-telemedicina-ltda/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-16 cursor-pointer"
                  onClick={() => trackClick("reclame_aqui")}
                >
                  <img
                    alt="Reclame aqui"
                    loading="lazy"
                    decoding="async"
                    className="h-full"
                    src="/reclame-aqui.webp"
                  />
                </a>
              </div>

              <div className="flex items-center gap-2 font-sans text-sm"></div>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-12 flex flex-col gap-6 pb-1 sm:pb-4 md:flex-row md:items-end md:justify-between">
          {/* Company Info */}
          <div>
            <div className="mb-2 sm:mb-4 flex flex-col items-left">
              <LeafIcon className="h-3 w-3 sm:h-5 sm:w-5 text-primary-darker mb-2 sm:mb-4" />
              <span className="text-primary-darker text-2xl font-semibold">
                <img
                  loading="lazy"
                  decoding="async"
                  src={"/logo.webp"}
                  alt={"Logo"}
                  className="h-3 sm:h-6 w-auto"
                />
              </span>
            </div>

            <div className="font-sans text-[10px] sm:text-sm text-black">
              nativamed LTDA
              <br />
              CNPJ 48.029.775/0001-16
              <br />
              © 2026 nativamed. Todos os direitos reservados
            </div>
          </div>

          {/* Legal links */}
          <div className="hidden sm:flex gap-6 font-sans text-sm">
            <a href="/politica-de-privacidade" className="hover:underline">
              Política de privacidade
            </a>
            <a href="/termos-de-uso" className="hover:underline">
              Termos de uso
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}